import { useState } from "react";
import * as Joi from "joi";
import DataPicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Text, Stack, Flex } from "@chakra-ui/react";

import {
  PostInCandidateAddApiArg,
  usePostInCandidateAddMutation,
} from "@ud/api";
import { useAlertModal } from "modals/useAlertModal";
import { useLoginModal } from "modals/useLoginModal";
import { useMessageSentModal } from "modals/useMessageSentModal";
import { AVAILABLE_FROM } from "@ud/config/availableForm";
import { useTracking } from "contexts/Tracking";
import StartConversationApiEndpoint from "utils/messages/api/StartConversation";
import styles from "./index.module.css";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import Button from "components/Chakra/Button";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Select from "components/Chakra/Selectbox";
import Textarea from "components/Chakra/Textarea";
import CustomLink from "components/Chakra/CustomLink";
import useForm, { HiddenInput, OnFormSubmitType } from "hooks/useForm";
import useApiEndpoint from "hooks/useApiEndpoint";
import useGlobalState from "hooks/useGlobalState";
import { parsePhoneStringToObject } from "helpers/phoneUtils";

export interface IFormValues {
  email: string;
  firstName: string;
  lastName: string;
  message: string;
  includePersonalProfile: boolean;
  phone: { number: string; prefix: string };
  dateOfBirth?: Date | null;
  availableFrom: PostInCandidateAddApiArg["body"]["availableFrom"];
}
interface IProps {
  offerId: number;
}

const FormSchema = Joi.object({
  email: Joi.string().email({ tlds: false }).required(),
  message: Joi.string().min(10).required(),
  lastName: Joi.string().min(2).max(30).required(),
  firstName: Joi.string().min(2).max(30).required(),
  includePersonalProfile: Joi.boolean(),
  dateOfBirth: Joi.date()
    .max(new Date())
    .greater(new Date().setFullYear(1899))
    .iso()
    .required(),
  availableFrom: Joi.string().valid("asap", "month", "later").required(),
  phone: JoiPhoneValidator(true),
});

const FormIN: React.FC<IProps> = ({ offerId }) => {
  const { trackEvent } = useTracking();
  const { user } = useGlobalState();
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const { callEndpoint: startConversationApi } = useApiEndpoint(
    StartConversationApiEndpoint,
  );
  const [postFormDataApi] = usePostInCandidateAddMutation();
  const { openModal: openAlertModal } = useAlertModal();
  const { openModal: openLoginModal } = useLoginModal();
  const { openModal: openMessageSentModal } = useMessageSentModal();

  const initialValues: IFormValues = {
    email: user ? user.email || "" : "",
    message: user?.renter?.informations || "",
    firstName: user?.renter?.firstName || "",
    lastName: user?.renter?.lastName || "",
    includePersonalProfile: true,
    phone:
      user && user.renter?.phone
        ? user.renter.phone
        : { number: "", prefix: "+420" },
    dateOfBirth: null,
    availableFrom: "none",
  };

  const {
    registerWithError,
    handleSubmit,
    reset,
    setValue,
    watch,
    errors,
    clearErrors,
  } = useForm<IFormValues>(FormSchema, {
    onLoginChange: ({ user: globalUser, form }) => {
      if (globalUser) {
        form.setValue("email", globalUser.email);
        setValue("phone", parsePhoneStringToObject(globalUser.phone));
      }
    },
    defaultValues: initialValues,
  });

  const selectForm = watch("availableFrom");
  const phoneNumber = watch("phone.number");
  const phonePrefix = watch("phone.prefix");

  const callApiReq = async (values: IFormValues) => {
    try {
      await startConversationApi({
        email: user?.email ? values.email : user?.email,
        message: values.message,
        firstName: values.firstName,
        lastName: values.lastName,
        displayCard: values.includePersonalProfile,
        offerId,
        telephone: phonePrefix + phoneNumber,
      });
      await postFormDataApi({
        body: {
          offerId,
          email: values.email || user?.email || "",
          message: values.message,
          firstName: values.firstName,
          lastName: values.lastName,
          phone: phonePrefix + phoneNumber,
          availableFrom: values.availableFrom,
          acceptTerms: true,
          dateOfBirth: values.dateOfBirth
            ? values.dateOfBirth.toISOString()
            : "",
        },
      }).unwrap();
      openMessageSentModal();
      setDateValue(null);
      reset();
    } catch (error) {
      openAlertModal({
        animation: "error",
        title: "Něco se pokazilo",
      });
    }
  };

  const onMessageSend = async (values: IFormValues): Promise<void> => {
    trackEvent({
      "dl.eCat": "Kontaktovat",
      "dl.eAct": "odeslat",
      "dl.eLab": "detail_inzeratu_click",
    });

    if (user?.email || values.email) {
      callApiReq(values);
    }
  };
  const handleData: OnFormSubmitType<IFormValues> = async (values) => {
    if (!user) {
      openLoginModal({
        initialEmail: values.email,
        onSuccessfulLogin: () => onMessageSend(values),
      });

      return;
    }
    onMessageSend(values);
  };

  return (
    <Box>
      {!user && (
        <Stack direction="row" mt={4} mb="5px">
          <Text variant="small_2">
            Máte účet?{" "}
            <Button variant="link" size="sm" onClick={() => openLoginModal({})}>
              Přihlaste se
            </Button>
            .
          </Text>
        </Stack>
      )}
      <form onSubmit={handleSubmit(handleData)}>
        <Stack spacing={0} mt="10px">
          {!user && (
            <AnimatedInput
              {...registerWithError("email")}
              data-test="offerDetail.contactIN.email"
              type="email"
              autoComplete="email"
              label="Váš email"
              required
            />
          )}
          {user && <HiddenInput {...registerWithError("email")} />}
          <PhoneInput
            required
            error={errors.phone}
            setValue={setValue}
            selectBoxValue={phonePrefix}
            phoneNumberValue={phoneNumber}
            clearErrors={clearErrors}
            dataTest="offerDetail.contactIN.phone"
          />
          <Flex gap={2}>
            <AnimatedInput
              {...registerWithError("firstName")}
              type="text"
              label="Jméno"
              required
              data-test="offerDetail.contactIN.name"
            />
            <AnimatedInput
              {...registerWithError("lastName")}
              label="Příjmení "
              type="text"
              required
              data-test="offerDetail.contactIN.surname"
            />
          </Flex>
          <Stack direction="row" className={styles.loop}>
            <Stack flex={1}>
              <DataPicker
                className={`datePicker ${styles.whiteColor}`}
                placeholderText="Dat. narození *"
                showYearDropdown
                dateFormat="dd.MM.yyyy"
                maxDate={new Date()}
                showMonthDropdown
                yearDropdownItemNumber={100}
                scrollableYearDropdown
                selected={dateValue}
                onChange={(date) => {
                  clearErrors("dateOfBirth");
                  if (date instanceof Date) {
                    setDateValue(date);
                    setValue("dateOfBirth", date);
                  }
                }}
                onFocus={(e) => {
                  e.target.placeholder = "dd.mm.yyyy";
                }}
                onBlur={(e) => {
                  e.target.placeholder = "Dat. narození *";
                }}
                customInput={
                  <AnimatedInput
                    className="datePickerInput"
                    {...registerWithError("dateOfBirth")}
                    type="text"
                    required
                    data-test="offerDetail.contactIN.date"
                  />
                }
              />
            </Stack>
            <Stack flex={1}>
              <Select
                {...registerWithError("availableFrom")}
                customError="any.required"
                backgroundColor="ud-white"
                color={
                  selectForm?.includes("none") ? "ud-grey-350" : "ud-grey-600"
                }
                _focus={{
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "ud-grey-500",
                }}
                data-test="offerDetail.contactIN.when"
              >
                <option disabled value="none">
                  Bydlet chci? *
                </option>
                {AVAILABLE_FROM.map((d) => (
                  <option
                    key={d.translationId}
                    value={d.apiId}
                    disabled={d.apiId === "none"}
                  >
                    {d.translationId}
                  </option>
                ))}
              </Select>
            </Stack>
          </Stack>
          <Stack>
            <Textarea
              _focus={{
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: "ud-grey-500",
              }}
              {...registerWithError("message")}
              bg="ud-white"
              placeholder="Chcete nám ještě něco vzkázat? *"
              data-test="offerDetail.contactIN.message"
            />
          </Stack>
          <Text
            mb="20px"
            mt="10px"
            variant="small_2"
            data-test="offerDetail.contactIN.gdpr"
          >
            Kliknutím souhlasíte s našimi{" "}
            <CustomLink
              showUnderline
              to="/zpracovani-osobnich-udaju"
              data-test="offerDetail.contactIN.gdpr.link"
            >
              zásadami ochrany osobních údajů.
            </CustomLink>
          </Text>
          <Stack pt={4} w="100%">
            <Button
              w="100%"
              type="submit"
              data-test="offerDetail.contactIN.submit"
            >
              Odeslat
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default FormIN;
